import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { TOPICS, getTopic, redistribute, normTerm, seasonWord, seasonWindow, VOICE_POSITIONS, voiceLabel } from '@gogrov/shared'
import { PageChrome, ROUTES, useIsDesktop } from '@/components/gg/chrome'
import { useTopics, usePlan } from '@/hooks/useConfig'
import { useSeedBalance, SEED_COSTS } from '@/hooks/useSeeds'
import { useProfile, useUpdatePrefs } from '@/hooks/useProfile'
import { useEffectiveClientId } from '@/hooks/useAuth'
import {
  useSaveMix, useResetMix, useAddPill, useRemovePill, useRenamePill, useResearchPills, useDismissSeason,
  useClientPrefs, useUpdateClientPrefs, type MixEntry,
} from '@/hooks/useMix'
import {
  useControlPills, useSetPillRest, useFeedNudges, useUndoNudge, useNextPlan, dropOrphanPills, feedRefreshUrl,
} from '@/hooks/useControls'
import { SeedCost } from '@/components/gg/SeedChip'
import { EmmaMini } from '@/components/gg/EmmaMini'
import { topicBackground, TOPIC_POS } from '@/components/gg/TopicScene'
import { tossSeeds, useSeedFx } from '@/lib/seedFx'
import type { Voice } from '@/types/database'
import type { ControlPill } from '@/types/controls'
import { Sheet, useToast, useLocalFlag, Icon } from './ui'
import {
  stateOf, typeOf, isStale, needsResearch, counts, waitingIds, suggestedWake, iso, fmt, fmtNum, today, addD, cap1,
  BAND_WORDS, BAND_SHORT, STALE_DAYS, DAY, type PillState,
} from './pillState'

/**
 * Topics (Brian's after/settings-topics.html v3.1, with DEVELOPER-HANDOFF Oct 22):
 * the topic mix as a 100% budget (a slider shares out the rest across the
 * unlocked topics; a lock pins a share; × gives it back evenly; at least two),
 * each topic a photo card with its controls on frosted glass and its share at
 * the slider's end; every topic folds. No Save: every valid change saves itself,
 * and Pull now rebuilds Your feed from the new mix. Under each slider the voice
 * and the keyword pills: their type, trend, season and the rest clock. The pill
 * pool is the plan's (24 · 48 · 96), shared across topics; resting pills and
 * places don't count. Showing fewer lists the nudges from the feed, each undoable.
 *
 * Brian dropped the per-topic reach rungs (City … Global) on Sep 30; the
 * column stays on the server, defaulted, and nothing here sets it.
 */

type Entry = { topic: string; share: number; locked: boolean; voice: Voice }

type SheetMode =
  | { kind: 'pill'; id: string; mode?: 'clock' }
  | { kind: 'realloc'; id: string }
  | { kind: 'room'; for: 'add'; topic: string; term: string }
  | { kind: 'room'; for: 'wake'; id: string }

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`
const VOICE_REMIND: { id: Voice; claim: string }[] = [
  { id: 'newcomer', claim: 'Claims none. Asks the question a new arrival would ask.' },
  { id: 'reporter', claim: 'Claims the record’s. Says what happened, what’s next.' },
  { id: 'insider', claim: 'Claims personal standing. Points a direction, no verdict.' },
]
const LESS = ['', 'a little', 'less', 'a lot less']

export function TopicsBoard() {
  const clientId = useEffectiveClientId()
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const { data: rows } = useTopics()
  const { data: plan } = usePlan()
  const nextPlan = useNextPlan()
  const { data: balance } = useSeedBalance()
  const { data: profile } = useProfile()
  const updatePrefs = useUpdatePrefs()
  const { data: prefs } = useClientPrefs()
  const { data: pillsRaw } = useControlPills()
  const { data: nudges } = useFeedNudges()
  const undoNudge = useUndoNudge()
  const save = useSaveMix()
  const reset = useResetMix()
  const addPill = useAddPill()
  const removePill = useRemovePill()
  const renamePill = useRenamePill()
  const research = useResearchPills()
  const dismiss = useDismissSeason()
  const setRest = useSetPillRest()
  const releaseHold = useSeedFx((s) => s.release)
  const [toastNode, toast] = useToast()
  const desktop = useIsDesktop()

  const cap = plan?.max_topics ?? 5
  const pillCap = plan?.max_pills ?? 24
  const now = useMemo(() => today(), [])
  const seeds = balance?.total_available ?? 0

  // ---------- the mix, edited here and saved whenever it's valid ----------
  const [mix, setMix] = useState<Entry[]>([])
  const dirty = useRef(false)
  const loaded = useRef(false)
  useEffect(() => {
    if (!rows || dirty.current) return
    const server: Entry[] = rows.map((r) => ({ topic: r.topic, share: r.share ?? 0, locked: !!r.locked, voice: (r.voice as Voice) ?? 'newcomer' }))
    setMix((cur) => {
      if (!loaded.current || cur.length === 0) {
        loaded.current = true
        return [...server].sort((a, b) => b.share - a.share || a.topic.localeCompare(b.topic))
      }
      // keep the order on screen; take the server's values
      const by = new Map(server.map((e) => [e.topic, e]))
      const kept = cur.filter((e) => by.has(e.topic)).map((e) => by.get(e.topic)!)
      const added = server.filter((e) => !cur.some((c) => c.topic === e.topic))
      return [...kept, ...added]
    })
  }, [rows])

  const total = mix.reduce((n, e) => n + e.share, 0)
  const valid = total === 100 && mix.length <= cap && mix.length >= 2
  const savingRef = useRef<Promise<void> | null>(null)
  const timer = useRef<number | undefined>(undefined)

  const doSave = useCallback(async (m: Entry[]) => {
    const p = (async () => {
      await save.mutateAsync(m.map((e) => ({ ...e })) as MixEntry[])
      dirty.current = false
      if (clientId) await dropOrphanPills(clientId, m.map((e) => e.topic))
    })()
    savingRef.current = p
    try { await p } finally { if (savingRef.current === p) savingRef.current = null }
  }, [save, clientId])

  const mixRef = useRef(mix)
  mixRef.current = mix
  /** Saves now if there is anything waiting; resolves when the server has it. */
  const flush = useCallback(async () => {
    window.clearTimeout(timer.current)
    const m = mixRef.current
    const ok = m.reduce((n, e) => n + e.share, 0) === 100 && m.length <= cap && m.length >= 2
    if (dirty.current && ok) await doSave(m)
    else if (savingRef.current) await savingRef.current
  }, [cap, doSave])

  function change(next: Entry[]) {
    dirty.current = true
    setMix(next)
    window.clearTimeout(timer.current)
    const ok = next.reduce((n, e) => n + e.share, 0) === 100 && next.length <= cap && next.length >= 2
    if (ok) timer.current = window.setTimeout(() => { void doSave(next).catch((e) => toast((e as Error).message, 3600)) }, 650)
  }
  useEffect(() => () => window.clearTimeout(timer.current), [])

  function move(topic: string, v: number) {
    const heldSet = new Set(mix.filter((_, i) => i >= cap).map((e) => e.topic))
    const locked = new Set(mix.filter((e) => e.locked || heldSet.has(e.topic)).map((e) => e.topic))
    const next = redistribute(Object.fromEntries(mix.map((e) => [e.topic, e.share])), locked, topic, v)
    change(mix.map((e) => ({ ...e, share: next[e.topic] ?? e.share })))
  }
  function removeTopic(topic: string) {
    if (mix.length <= 2) { toast('Keep at least two topics.'); return }
    const gone = mix.find((e) => e.topic === topic)!
    const rest = mix.filter((e) => e.topic !== topic)
    const pool = rest.filter((e, i) => !e.locked && i < cap)
    const to = pool.length ? pool : rest
    const next = rest.map((e) => {
      const k = to.indexOf(e)
      return k < 0 ? e : { ...e, share: e.share + Math.floor(gone.share / to.length) + (k < gone.share % to.length ? 1 : 0) }
    })
    change(next)
    toast(<>{getTopic(topic)?.label} removed. Its {gone.share}% went back to the others.</>)
  }
  function addTopic(topic: string) {
    if (mix.length >= cap) return
    const withNew = [...mix, { topic, share: 0, locked: false, voice: 'newcomer' as Voice }]
    const locked = new Set(withNew.filter((e) => e.locked).map((e) => e.topic))
    const next = redistribute(Object.fromEntries(withNew.map((e) => [e.topic, e.share])), locked, topic, Math.round(100 / withNew.length))
    change(withNew.map((e) => ({ ...e, share: next[e.topic] ?? e.share })))
    toast(<>{getTopic(topic)?.label} added. Drag its slider to set its share.</>)
    window.setTimeout(() => document.querySelector(`[data-topic-card="${topic}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 80)
  }
  function setVoice(topic: string, voice: Voice) {
    change(mix.map((e) => (e.topic === topic ? { ...e, voice } : e)))
    toast(<>{getTopic(topic)?.label} now writes as <b>{voiceLabel(voice)}</b> by default.</>)
  }

  async function onPull() {
    if (!valid) {
      toast(mix.length > cap
        ? <><b className="al">{plan?.name} holds {cap} topics.</b> Remove {mix.length - cap} first.</>
        : mix.length < 2 ? <><b className="al">Pick at least two topics.</b></>
        : <><b className="al">The mix is {total}%.</b> It needs exactly 100.</>, 3200)
      return
    }
    try { await flush(); navigate(feedRefreshUrl()) } catch (e) { toast((e as Error).message, 3600) }
  }
  async function onReset() {
    if (seeds < SEED_COSTS.reset_mix) { toast(<><b className="al">Not enough seeds left for that.</b> This week’s come back Monday.</>); return }
    try {
      await flush()
      tossSeeds(SEED_COSTS.reset_mix)
      await reset.mutateAsync()
      dirty.current = false
      toast('Shares are even again.')
    } catch (e) { releaseHold(); toast((e as Error).message, 3600) }
  }

  // ---------- pills ----------
  const [researching, setResearching] = useState<Set<string>>(new Set())
  const [fresh, setFresh] = useState<Set<string>>(new Set())
  const pills = useMemo(() => pillsRaw ?? [], [pillsRaw])
  const waiting = useMemo(() => waitingIds(pills, now, pillCap), [pills, now, pillCap])
  const stOf = useCallback((p: ControlPill) => stateOf(p, now, { researching, waiting }), [now, researching, waiting])
  const active = pills.filter((p) => counts(stOf(p).s)).length
  const restingN = pills.filter((p) => stOf(p).s === 'resting').length
  const pillById = (id: string) => pills.find((p) => p.id === id)

  // A seasonal pill out of season rests on its own: write its rest date so the
  // server's pool count agrees with what's on screen.
  const synced = useRef(new Set<string>())
  useEffect(() => {
    for (const p of pills) {
      const st = stateOf(p, now)
      if (st.s === 'resting' && !st.byHand && !p.rest_until && st.until && !synced.current.has(p.id)) {
        synced.current.add(p.id)
        setRest.mutate({ id: p.id, until: iso(st.until) })
      }
    }
  }, [pills, now, setRest])

  async function addKeyword(topic: string, term: string, roomMade = false) {
    const key = normTerm(term)
    if (!key) return
    if (pills.some((p) => normTerm(p.term) === key)) { toast(<>“{term}” is already one of your pills.</>); return }
    if (!roomMade && active >= pillCap) { setSheet({ kind: 'room', for: 'add', topic, term }); return }
    try {
      await flush()
      const id = await addPill.mutateAsync({ topic, term })
      setFresh((s) => new Set(s).add(id))
      const sw = getTopic(topic)?.seasonal ? seasonWord(term) : null
      const seaNote = sw ? ` A seasonal pill: its clock is set for ${fmt(seasonWindow(sw, now).wake)}.` : ''
      // what the server already knows about it (a place, or someone's reading)
      window.setTimeout(() => {
        const p = (pillsRef.current ?? []).find((x) => x.id === id)
        if (p?.pill_type === 'anchor') toast(<><b>{term}</b> is a place you cover: low volume, steady. Never researched, never charged.</>, 3400)
        else if (p?.researched_at && !p.stale) {
          const ago = Math.floor((Date.now() - new Date(p.researched_at).getTime()) / DAY)
          toast(<><b>{term}</b>: another account researched it {ago ? `${ago} days ago` : 'today'}, so its reading is yours, free.{seaNote}</>, 3600)
        } else toast(<>Added <b>{term}</b>, free. Check its trend any time for 1 seed.{seaNote}</>, 3600)
      }, 600)
    } catch (e) {
      const m = (e as Error).message
      if (/pool/i.test(m)) setSheet({ kind: 'room', for: 'add', topic, term })
      else toast(m, 3600)
    }
  }
  const pillsRef = useRef(pillsRaw)
  pillsRef.current = pillsRaw

  async function researchPills(list: ControlPill[]) {
    const n = list.length
    if (!n) return
    if (seeds < n) { toast(<><b className="al">Not enough seeds left for that.</b> This week’s come back Monday.</>, 3400); return }
    setResearching((s) => { const x = new Set(s); list.forEach((p) => x.add(p.id)); return x })
    tossSeeds(n)
    try {
      const res = await research.mutateAsync(list.map((p) => p.id))
      if (!res.charged) releaseHold()
      setFresh((s) => { const x = new Set(s); list.forEach((p) => x.add(p.id)); return x })
      window.setTimeout(() => {
        const after = (pillsRef.current ?? []) as ControlPill[]
        const one = after.find((p) => p.id === list[0].id)
        toast(n === 1 && one ? <><b>{one.term}</b>: {typeOf(one, stateOf(one, now)).l}.</> : `${n} keywords rechecked.`, 3400)
      }, 400)
    } catch (e) { releaseHold(); toast((e as Error).message, 3600) }
    finally { setResearching((s) => { const x = new Set(s); list.forEach((p) => x.delete(p.id)); return x }) }
  }

  // ---------- the sheets ----------
  const [sheet, setSheet] = useState<SheetMode | null>(null)
  const [open, setOpen] = useState(false)
  useEffect(() => { setOpen(!!sheet) }, [sheet])
  const closeSheet = useCallback(() => { setOpen(false); window.setTimeout(() => setSheet(null), 260) }, [])

  // ?pill=<id> (Emma's season note elsewhere): open that pill
  const focusPill = params.get('pill')
  useEffect(() => {
    if (!focusPill || !pillsRaw) return
    const p = pillsRaw.find((x) => x.id === focusPill)
    const rest = new URLSearchParams(params); rest.delete('pill'); setParams(rest, { replace: true })
    if (!p) return
    window.setTimeout(() => {
      document.querySelector(`[data-pill="${p.id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      setSheet({ kind: 'pill', id: p.id })
    }, 350)
  }, [focusPill, pillsRaw, params, setParams])

  // ---------- folding, the key, the note from Emma ----------
  const [folded, setFolded] = useLocalFlag<Record<string, boolean>>(`gg.topics.folded.${clientId ?? ''}`, {})
  const [keyHidden, setKeyHidden] = useLocalFlag<boolean>('gg.topics.keyHidden', false)
  const [nudgeDone, setNudgeDone] = useLocalFlag<boolean>(`gg.topics.seasonNudge.${clientId ?? ''}`, false)
  const closingNow = pills.filter((p) => stOf(p).s === 'closing' && !p.season_dismissed)
  const snoozed = !!profile?.season_snooze_until && new Date(profile.season_snooze_until).getTime() > Date.now()
  const showNudge = !nudgeDone && !snoozed && closingNow.length > 0 && !sheet
  // Brian's log #016 ("Emma should still be visible when switching pages, guiding you to the section"): after
  // Take me there now, Emma stays on screen above the pill she sent you to, "1 of 2 · Next", and walks you
  // through each one; Done lets her go.
  const [guide, setGuide] = useState<{ list: { id: string; term: string }[]; i: number } | null>(null)
  function guideTo(list: { id: string; term: string }[], i: number) {
    setGuide({ list, i })
    const f = list[i]
    document.querySelector(`[data-pill="${f.id}"]`)?.scrollIntoView({ block: 'center', behavior: 'smooth' })
    window.setTimeout(() => setSheet({ kind: 'pill', id: f.id }), 350)
  }

  const keyRef = useRef<HTMLDivElement>(null)
  const [remind, setRemind] = useState<string | null>(null)
  const [adding, setAdding] = useState<string | null>(null)

  const inMix = new Set(mix.map((e) => e.topic))
  const full = mix.length >= cap
  const politicsOff = prefs?.politics_mode === 'off'
  const saveLine = valid
    ? (save.isPending ? 'Saving' : 'Saved · next pull')
    : mix.length > cap ? `Remove ${mix.length - cap} to save` : mix.length < 2 ? 'Two topics to save' : `${total}% · needs 100`

  // Phone row 2 has room for one action: Pull now goes there, Reset mix sits under the totals.
  const resetBtn = (
    <button type="button" className={desktop ? 'gg-act' : 'fx-reset'} onClick={() => void onReset()} disabled={reset.isPending || mix.length === 0}>
      Reset mix<SeedCost n={SEED_COSTS.reset_mix} />
    </button>
  )

  return (
    <div className="fx-page fx-topics">
      <PageChrome
        title="Topics" navKey="topics" back={{ to: ROUTES.feedControls, label: 'Feed controls' }}
        actions={<>
          {desktop && resetBtn}
          <button type="button" className={`gg-act gg-act--amber${valid ? '' : ' is-dim'}`} aria-disabled={!valid} onClick={() => void onPull()}>
            {valid ? 'Pull now' : mix.length > cap ? `Remove ${plural(mix.length - cap, 'topic')}` : mix.length < 2 ? 'Two topics' : `${total}% · needs 100`}
          </button>
        </>}
      />

      <div className="fx-head">
        <h1>Topic mix<small>Your brief, shared out. Moving one slider shares the rest.</small></h1>
        <div className="fx-alloc" aria-live="polite">
          <div className={`tot${total === 100 ? '' : ' off'}`}>{total}%</div>
          <div className="row"><span>Topics</span><b className={mix.length > cap ? 'off' : ''}>{mix.length} of {cap}</b></div>
          <div className="row"><span>Keywords</span><b className={active >= pillCap ? 'off' : ''}>{Math.max(0, pillCap - active)} of {pillCap}</b></div>
          <div className="row sub"><span /><span>available</span></div>
          <div className="row"><span>Paused</span><b>{restingN}</b></div>
          <button className="types" type="button" onClick={() => { setKeyHidden(!keyHidden); if (keyHidden) window.setTimeout(() => keyRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }), 50) }}>What the labels mean</button>
          <div className={`saved${valid ? '' : ' off'}`}>{saveLine}</div>
          {!desktop && resetBtn}
        </div>
      </div>

      {showNudge && (
        <div className="fx-emsay" role="region" aria-label="Emma">
          <div className="who">Emma</div>
          <p>
            {closingNow.length === 1 ? 'One of your pills is heading out of season' : `${closingNow.length === 2 ? 'A couple' : 'A few'} of your pills are heading out of season`}:{' '}
            {joinNames(closingNow.map((p) => cap1(p.term)))}. {closingNow.length === 1 ? 'It’ll show up dimmed. Want to point it' : 'They’ll show up dimmed. Want to point them'} at what’s next?
          </p>
          <div className="btns">
            <button className="pri" type="button" onClick={() => {
              setNudgeDone(true)
              guideTo(closingNow.map((p) => ({ id: p.id, term: p.term })), 0)
            }}>Take me there now</button>
            <button type="button" onClick={() => {
              updatePrefs.mutate({ season_snooze_until: addD(new Date(), 3).toISOString() })
              toast(`Emma will bring it up again ${fmt(addD(now, 3))}. The glow stays on them till then.`, 3200)
            }}>Remind me later</button>
            <button className="ghost" type="button" onClick={() => {
              setNudgeDone(true)
              if (profile) closingNow.forEach((p) => dismiss.mutate({ userId: profile.id, topic: p.topic, termNorm: p.term_norm }))
              toast('Got it. They’ll dim on their own when their seasons close.', 3000)
            }}>No thanks</button>
          </div>
        </div>
      )}

      {!keyHidden && (
        <div className="fx-key" ref={keyRef}>
          <div className="kh"><b>What the keyword labels mean</b><button type="button" onClick={() => setKeyHidden(true)}>Hide</button></div>
          <KeyRow c="t-up" term="Deschutes Crossing" l="Well targeted ↑">One named thing: a person, a venue, a company. Movement has a cause, so it carries an arrow against its own past. ↓ means quieter than it usually is, never worse than another keyword.</KeyRow>
          <KeyRow c="t-broad" term="live music" l="Too broad">An umbrella over a hundred unrelated stories. A trend here would average to nothing, so there is no arrow.</KeyRow>
          <KeyRow c="t-anchor" term={(prefs?.city_state ?? 'Bend').split(',')[0]} l="Low volume, steady">A place from your coverage area. No arrow, never goes stale, never costs a seed to recheck, and is never deleted for being quiet.</KeyRow>
          <KeyRow c="t-watch" term="The Hollow Pines Band" l="Not enough coverage yet">The system has no data on this term yet. It resolves on its own once coverage appears.</KeyRow>
          <div className="kf">The arrow shows whether a keyword is in the news more or less than usual. Adding or removing a keyword is free; checking its trend uses 1 seed.</div>
        </div>
      )}

      {(nudges?.length ?? 0) > 0 && (
        <div className="fx-fewer">
          <div className="k">Showing fewer</div>
          <div className="p">From Fewer like this on your brief. A nudge, never a block: you still see some.</div>
          {nudges!.map((n) => (
            <div className="r" key={`${n.kind}:${n.key}`}>
              <span>{n.label}<small>{LESS[Math.min(3, n.n)] || 'less'}</small></span>
              <button type="button" onClick={() => undoNudge.mutate(n, { onSuccess: () => toast(<>Back to normal: <b>{n.label}</b>.</>) })}>Undo</button>
            </div>
          ))}
        </div>
      )}

      <div className="fx-cards">
        {mix.map((e, i) => {
          const t = getTopic(e.topic)
          const label = t?.label ?? e.topic
          const held = i >= cap
          const isFolded = !!folded[e.topic]
          const mine = pills.filter((p) => p.topic === e.topic)
          const staleN = mine.filter((p) => isStale(p, stOf(p))).length
          const needN = mine.filter((p) => needsResearch(p, stOf(p)))
          return (
            <section key={e.topic} data-topic-card={e.topic} className={`fx-topic${e.locked ? ' locked' : ''}${held ? ' held' : ''}${isFolded ? ' folded' : ''}`}>
              <div className="ph" style={{ backgroundImage: topicBackground(e.topic), backgroundPosition: TOPIC_POS[e.topic] ?? 'center top' }} />
              <div className="band">
                <div className="top">
                  <button className="fold" type="button" aria-expanded={!isFolded} aria-label={`${isFolded ? 'Open' : 'Fold'} ${label}`}
                    onClick={() => setFolded({ ...folded, [e.topic]: !isFolded })}>{Icon.fold}</button>
                  <div className="nm" onClick={() => setFolded({ ...folded, [e.topic]: !isFolded })}>
                    {label}
                    {isFolded ? <small>{plural(mine.length, 'keyword')} · {voiceLabel(e.voice)}</small> : t?.seasonal ? <small>Seasonal topic</small> : null}
                  </div>
                  {isFolded && <span className="val">{e.share}%</span>}
                  <button className={`ib${e.locked ? ' on' : ''}`} type="button" aria-label={`${e.locked ? 'Unlock' : 'Lock'} ${label}`}
                    onClick={() => change(mix.map((x) => (x.topic === e.topic ? { ...x, locked: !x.locked } : x)))}>{e.locked ? Icon.lock : Icon.unlock}</button>
                  <button className="ib" type="button" aria-label={`Remove ${label}`} onClick={() => removeTopic(e.topic)}>{Icon.x}</button>
                </div>
              </div>
              {!isFolded && (
                <div className="glass">
                  <div className="rrow">
                    <input className="rng" type="range" min={0} max={100} step={1} value={e.share} disabled={e.locked || held}
                      aria-label={`${label} share`} style={{ ['--v' as string]: `${e.share}%` }}
                      onChange={(ev) => move(e.topic, Number(ev.target.value))} />
                    <span className="val">{e.share}%</span>
                  </div>
                  {held && <div className="heldnote">Over {plan?.name}’s {cap} topics · remove one to save</div>}
                  {politicsOff && t?.political && <div className="heldnote">Politics is off for this account, so this topic pulls nothing until it is switched back on.</div>}
                  <div className="vc">
                    <span className="lab">Voice<button className="i" type="button" aria-label="What the voice positions mean" aria-expanded={remind === e.topic}
                      onClick={() => setRemind(remind === e.topic ? null : e.topic)}>i</button></span>
                    <div className="seg" role="group" aria-label={`${label} voice`}>
                      {VOICE_POSITIONS.map((v) => (
                        <button key={v.id} type="button" aria-pressed={e.voice === v.id} onClick={() => setVoice(e.topic, v.id)}>{v.label}</button>
                      ))}
                    </div>
                  </div>
                  {remind === e.topic && (
                    <div className="remind">
                      <div className="eb">Voice positions · how much authority you claim</div>
                      {VOICE_REMIND.map((v) => <div className="r" key={v.id}><b>{voiceLabel(v.id)}</b><span>{v.claim}</span></div>)}
                      <div className="f"><span /><button type="button" onClick={() => setRemind(null)}>Close</button></div>
                    </div>
                  )}
                  {staleN > 0 && <div className="stalenote">{staleN} {staleN === 1 ? 'pill needs' : 'pills need'} a recheck</div>}
                  <div className="pills">
                    {mine.map((p) => (
                      <PillChip key={p.id} p={p} st={stOf(p)} fresh={fresh.has(p.id)}
                        onOpen={(mode) => setSheet({ kind: 'pill', id: p.id, mode })} />
                    ))}
                    {adding === e.topic ? (
                      <input className="addin" autoFocus placeholder="A keyword, then Enter" maxLength={60} aria-label={`Add a keyword to ${label}`}
                        onKeyDown={(ev) => {
                          const v = (ev.target as HTMLInputElement).value.trim()
                          if (ev.key === 'Enter' && v) { setAdding(null); void addKeyword(e.topic, v) }
                          if (ev.key === 'Escape') setAdding(null)
                        }}
                        onBlur={() => window.setTimeout(() => setAdding(null), 120)} />
                    ) : (
                      <button className="addpill" type="button" onClick={() => setAdding(e.topic)}>+ Keyword</button>
                    )}
                    {needN.length > 0 && (
                      <button className="recheck" type="button" disabled={research.isPending} onClick={() => void researchPills(needN)}>
                        {needN.length > 1 ? 'Recheck all' : 'Recheck'}<SeedCost n={needN.length * SEED_COSTS.pill_research} />
                      </button>
                    )}
                  </div>
                </div>
              )}
            </section>
          )
        })}
      </div>

      <div className="fx-more">
        <div className="k">Add a topic</div>
        <div className="chips">
          {TOPICS.filter((t) => !inMix.has(t.id)).map((t) => {
            const blocked = politicsOff && t.political
            return (
              <button key={t.id} type="button" className="tile" disabled={full || blocked}
                style={{ backgroundImage: topicBackground(t.id), backgroundPosition: TOPIC_POS[t.id] ?? 'center' }}
                aria-label={`Add ${t.label}`} title={blocked ? 'Politics is switched off for this account' : full ? `Your plan holds ${cap} topics` : `Add ${t.label}`}
                onClick={() => addTopic(t.id)}>
                <span className="plus">＋</span><span className="nm">{t.label}</span>
              </button>
            )
          })}
        </div>
      </div>

      <Gates />

      <Sheet open={open} onClose={closeSheet} label="Keyword">
        {sheet && (
          <PillSheetBody
            mode={sheet} pills={pills} mix={mix} stOf={stOf} now={now} pillCap={pillCap} active={active}
            planName={plan?.name ?? ''} nextPlan={nextPlan ? { name: nextPlan.name, pills: nextPlan.max_pills ?? 0, price: Math.round(nextPlan.price_monthly / 100) } : null}
            pillById={pillById} setSheet={setSheet} close={closeSheet} toast={toast}
            onRest={async (p, until) => { await setRest.mutateAsync({ id: p.id, until: iso(until) }) }}
            onWake={async (p) => {
              const auto = stateOf({ ...p, rest_until: null }, now).s === 'resting'
              await setRest.mutateAsync({ id: p.id, until: null, forceAwake: auto })
            }}
            onRemove={async (p) => { await removePill.mutateAsync(p.id) }}
            onResearch={(p) => void researchPills([p])}
            onRetarget={async (p, term) => {
              await renamePill.mutateAsync({ id: p.id, term }); setFresh((s) => new Set(s).add(p.id))
              await new Promise((r) => window.setTimeout(r, 500))
              const after = (pillsRef.current ?? []).find((x) => x.id === p.id)
              return !!after?.researched_at && !after.stale
            }}
            onLeave={async (p) => { if (profile) await dismiss.mutateAsync({ userId: profile.id, topic: p.topic, termNorm: p.term_norm }) }}
            onRealloc={async (p, toTopic, sh) => {
              await removePill.mutateAsync(p.id)
              change(mix.map((x) => (x.topic === p.topic ? { ...x, share: x.share - sh } : x.topic === toTopic ? { ...x, share: x.share + sh } : x)))
            }}
            onAddAfterRoom={(topic, term) => void addKeyword(topic, term, true)}
            onUpgrade={() => navigate(`${ROUTES.settings}?tab=plan`)}
            place={(prefs?.city_state ?? '').split(',')[0].trim() || 'your area'}
          />
        )}
      </Sheet>
      {guide && (() => {
        const { list, i } = guide, f = list[i], last = i === list.length - 1
        return (
          <div className="fx-eguide" role="status">
            <EmmaMini size={40} talk={i} />
            <span className="tx">
              <small>Emma · {list.length > 1 ? `${i + 1} of ${list.length}` : 'here it is'}</small>
              <b>{cap1(f.term)}</b>: point it at what’s next, or retire it.
            </span>
            {last
              ? <button type="button" className="gg-btn" onClick={() => { setGuide(null); toast('Nice. Your pills follow what’s next.', 2400) }}>Done</button>
              : <button type="button" className="gg-btn gg-btn--amber" onClick={() => guideTo(list, i + 1)}>Next: {cap1(list[i + 1].term)} ›</button>}
          </div>
        )
      })()}
      {toastNode}
    </div>
  )
}

function joinNames(names: string[]): ReactNode {
  const b = names.map((n, i) => <b key={i}>{n}</b>)
  if (b.length === 1) return b[0]
  if (b.length === 2) return <>{b[0]} and {b[1]}</>
  return <>{b.slice(0, -1).map((x, i) => <span key={i}>{x}{i < b.length - 2 ? ', ' : ''}</span>)} and {b[b.length - 1]}</>
}

function KeyRow({ c, term, l, children }: { c: string; term: string; l: string; children: ReactNode }) {
  return (
    <div className="kr">
      <span className={`pill ${c}`}><span className="term">{term}</span><span className="lbl">{l}</span></span>
      <p>{children}</p>
    </div>
  )
}

function PillChip({ p, st, fresh, onOpen }: { p: ControlPill; st: PillState; fresh: boolean; onOpen: (mode?: 'clock') => void }) {
  if (st.s === 'anchor') {
    return (
      <span className="pill t-anchor anchor" data-pill={p.id} title="A place in your coverage area. Never researched, never charged.">
        <span className="term">{p.term}</span><span className="lbl">Low volume, steady</span>
      </span>
    )
  }
  if (st.s === 'researching') {
    return <span className="pill researching" data-pill={p.id}><span className="term">{p.term}</span><span className="st">Researching</span><span className="clk" /></span>
  }
  const ty = typeOf(p, st)
  const stale = isStale(p, st)
  const tag = st.s === 'warming' ? 'Warming' : st.s === 'closing' && st.w ? `Ends ${fmtNum(st.w.close)}` : st.s === 'resting' && st.until ? `Back ${fmtNum(st.until)}` : st.s === 'waiting' ? 'Ready · no room' : null
  const glow = st.s === 'closing' && !p.season_dismissed
  return (
    <span className={`pill ${ty.c} ${st.s}${glow ? ' glow' : ''}${stale ? ' stale' : ''}${fresh ? ' fresh' : ''}`} data-pill={p.id}
      role="button" tabIndex={0} title={stale ? 'It has been a while since this was researched. Recheck to see if it is still trending.' : undefined}
      onClick={() => onOpen()} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen() } }}>
      <span className="term">{p.term}</span>
      {(st.s === 'plain' || st.s === 'in') && <span className="lbl">{ty.l}</span>}
      {tag && <span className="st">{tag}</span>}
      <button className={`clk${st.sea ? ' sea' : ''}`} type="button"
        aria-label={st.s === 'resting' ? 'Resting. Change or wake' : st.sea ? 'A seasonal pill. See when it rests and comes back' : 'Rest this pill'}
        onClick={(e) => { e.stopPropagation(); onOpen('clock') }}>{Icon.clock}</button>
    </span>
  )
}

// ---------------- the pill sheet: the reading, the clock, retarget, make room ----------------

interface SheetProps {
  mode: SheetMode
  pills: ControlPill[]
  mix: Entry[]
  stOf: (p: ControlPill) => PillState
  now: Date
  pillCap: number
  active: number
  planName: string
  nextPlan: { name: string; pills: number; price: number } | null
  pillById: (id: string) => ControlPill | undefined
  setSheet: (m: SheetMode | null) => void
  close: () => void
  toast: (b: ReactNode, ms?: number) => void
  onRest: (p: ControlPill, until: Date) => Promise<void>
  onWake: (p: ControlPill) => Promise<void>
  onRemove: (p: ControlPill) => Promise<void>
  onResearch: (p: ControlPill) => void
  /** Resolves true when another account's reading (under 60 days) came with it. */
  onRetarget: (p: ControlPill, term: string) => Promise<boolean>
  onLeave: (p: ControlPill) => Promise<void>
  onRealloc: (p: ControlPill, toTopic: string, share: number) => Promise<void>
  onAddAfterRoom: (topic: string, term: string) => void
  onUpgrade: () => void
  /** The home place, for "Strong coverage in Bend". */
  place: string
}

function PillSheetBody(props: SheetProps) {
  const { mode, pillById, stOf, now, close, toast } = props
  const [wakeDraft, setWakeDraft] = useState<Date | null>(null)
  const [busy, setBusy] = useState(false)
  const run = async (f: () => Promise<void>) => {
    if (busy) return
    setBusy(true)
    try { await f() } catch (e) {
      const m = (e as Error).message
      toast(/pool/i.test(m) ? 'Your keywords are full. Rest or remove one first.' : m, 3400)
    } finally { setBusy(false) }
  }

  if (mode.kind === 'room') return <RoomPicker {...props} mode={mode} run={run} />

  const p = pillById(mode.id)
  if (!p) return null
  const st = stOf(p)
  const label = getTopic(p.topic)?.label ?? p.topic
  const entry = props.mix.find((e) => e.topic === p.topic)

  if (mode.kind === 'realloc') {
    const sh = shareOf(p, entry, props.pills)
    const to = props.mix.filter((x) => x.topic !== p.topic)
    return (
      <div className="fx-in">
        <div className="eb">Reallocate · {cap1(p.term)}</div>
        <h2>Move {sh}% to…</h2>
        <p className="lead">The pill retires and its slice of {label} ({entry?.share ?? 0}% across {props.pills.filter((x) => x.topic === p.topic && x.pill_type !== 'anchor').length} pills) goes to the topic you pick.</p>
        <div className="acts">
          {to.map((x) => (
            <button key={x.topic} className="opt" type="button" disabled={busy} onClick={() => run(async () => {
              await props.onRealloc(p, x.topic, sh); close()
              toast(<><b>{p.term}</b> retired. {sh}% moved from {label} to <b>{getTopic(x.topic)?.label}</b>.</>, 3600)
            })}>
              <span className="t"><b>{getTopic(x.topic)?.label}</b><small>{x.share}% → {x.share + sh}%{x.locked ? ' · locked, takes it anyway' : ''}</small></span>
            </button>
          ))}
          <button className="btn ghost" type="button" onClick={() => props.setSheet({ kind: 'pill', id: p.id })}>Back</button>
        </div>
      </div>
    )
  }

  if (st.s === 'waiting') return <RoomPicker {...props} mode={{ kind: 'room', for: 'wake', id: p.id }} run={run} />

  const sug = suggestedWake(p, now)
  if (mode.mode === 'clock' || st.s === 'resting') {
    const resting = st.s === 'resting'
    const draft = wakeDraft ?? (resting && st.until ? st.until : sug)
    const isSug = iso(draft) === iso(sug)
    const seasonal = !!st.sea || !!stateOf({ ...p, rest_until: null }, now).sea
    return (
      <div className="fx-in">
        <div className="eb">{resting ? 'Resting' : 'Rest'} · {label}</div>
        <h2>{p.term}</h2>
        <p className="lead">{resting ? 'It isn’t steering your brief and doesn’t count against your pills. It comes back on its own.' : 'It stops steering your brief and stops counting against your pills, then comes back on its own.'}</p>
        <div className="clock">
          <span className="face">{Icon.clock}</span>
          <div className="d">
            <b>{resting ? 'Back' : 'Until'} {fmt(draft)}</b>
            <small>{isSug ? (seasonal ? 'Suggested: when coverage usually starts building' : 'Suggested: four weeks') : `${Math.round((draft.getTime() - now.getTime()) / DAY)} days from today`}</small>
            {!isSug && <button className="sug" type="button" onClick={() => setWakeDraft(sug)}>Use the suggested date</button>}
          </div>
          <button className="step" type="button" aria-label="A week earlier" onClick={() => { const d = addD(draft, -7); if (d > now) setWakeDraft(d) }}>−</button>
          <button className="step" type="button" aria-label="A week later" onClick={() => setWakeDraft(addD(draft, 7))}>+</button>
        </div>
        <div className="acts">
          <button className="btn pri" type="button" disabled={busy} onClick={() => run(async () => {
            await props.onRest(p, draft); close(); toast(<><b>{p.term}</b> is resting. It comes back {fmt(draft)}.</>)
          })}>{Icon.clock}{resting ? `Change to ${fmt(draft)}` : `Rest until ${fmt(draft)}`}</button>
          {resting && (
            <button className="btn" type="button" disabled={busy} onClick={() => run(async () => {
              await props.onWake(p); close(); toast(<><b>{p.term}</b> is back and steering your brief.</>)
            })}>Wake it now</button>
          )}
          <button className="btn ghost" type="button" disabled={busy} onClick={() => run(async () => {
            await props.onRemove(p); close(); toast(`${p.term} removed.`)
          })}>Remove instead</button>
        </div>
      </div>
    )
  }

  const rd = st.r
  const succ = st.sea?.next ?? p.successors ?? []
  if (st.s === 'closing' && st.w) {
    const nm = cap1(p.term)
    const sh = shareOf(p, entry, props.pills)
    const others = props.mix.filter((x) => x.topic !== p.topic).length
    return (
      <div className="fx-in">
        <div className="eb">{label} · pill</div>
        <h2>{nm}</h2>
        <div className="line"><span className="k">Season</span><span><b>Closes {fmt(st.w.close)}</b>. It dims after that and comes back around {fmt(sug)}.</span></div>
        <p className="q">{succ.length ? `${nm} is wrapping up. Want to point this at ${succ[0]}?` : `${nm} is wrapping up. Nothing reliably follows it, so choose what happens to its share.`}</p>
        <div className="acts">
          {succ.slice(0, 2).map((s, k) => (
            <button key={s} className={`opt${k ? '' : ' pri'}`} type="button" disabled={busy} onClick={() => run(async () => {
              if (props.pills.some((q) => normTerm(q.term) === normTerm(s))) { toast(<>“{s}” is already one of your pills.</>); return }
              const shared = await props.onRetarget(p, s); close()
              toast(<>Retargeted <b>{p.term}</b> to <b>{s}</b>. Same share, same topic{shared ? ', and its reading is shared, free' : '. It lands unresearched; Research gives it its arrow'}.</>, 3800)
            })}>
              <span className="t"><b>Retarget to {s}</b><small>{k ? 'the other usual successor' : 'keeps your share and settings'}</small></span>
              {!k && <span className="tag">Preferred</span>}
            </button>
          ))}
          <button className="opt" type="button" disabled={busy || !!entry?.locked || !others} onClick={() => props.setSheet({ kind: 'realloc', id: p.id })}>
            <span className="t"><b>Reallocate the share</b><small>{entry?.locked ? `${label} is locked: unlock it to move its share` : `move this pill’s ${sh}% to another topic`}</small></span>
          </button>
          <button className="opt" type="button" disabled={busy} onClick={() => run(async () => {
            await props.onRemove(p); close(); toast(<><b>{p.term}</b> retired. Its slot is free.</>)
          })}><span className="t"><b>Retire the pill</b><small>free the slot</small></span></button>
          <button className="opt" type="button" disabled={busy} onClick={() => run(async () => {
            await props.onLeave(p); close(); toast(<><b>{p.term}</b> stays. It dims after {fmt(st.w!.close)} and comes back around {fmt(sug)}.</>, 3400)
          })}><span className="t"><b>Leave it</b><small>it stays dimmed until {fmt(sug)}</small></span></button>
          <button className="btn ghost" type="button" onClick={() => { close(); props.onResearch(p) }}>{rd ? 'Recheck' : 'Research'}<SeedCost n={SEED_COSTS.pill_research} /></button>
        </div>
      </div>
    )
  }

  const place = props.place
  const trendW = rd?.type === 'broad' ? 'A broad term: it gathers many unrelated stories, so it carries no arrow.'
    : rd?.type === 'watching' ? 'Too little coverage to judge yet.'
    : rd?.trend === 'up' ? 'Rising against its own last month.' : rd?.trend === 'down' ? 'Falling against its own last month.' : 'Steady against its own last month.'
  const seaW = st.sea && st.w ? `${fmt(st.w.open)} – ${fmt(st.w.close)}. Coverage starts building around ${fmt(st.w.wake)}.`
    : getTopic(p.topic)?.seasonal && rd?.type === 'broad' ? 'A broad term on a seasonal topic reads as always in season.' : 'No season. Runs all year.'
  const stateW = st.s === 'in' ? 'In season' : st.s === 'warming' ? 'Warming up: coverage is building' : 'Active'
  return (
    <div className="fx-in">
      <div className="eb">{label} · {typeOf(p, st).l}</div>
      <h2>{p.term}</h2>
      <div className="line"><span className="k">Now</span><span><b>{stateW}</b></span></div>
      <div className="line"><span className="k">Coverage</span><span>{rd ? `${BAND_WORDS[rd.band]} in ${place}, last 30 days. ${trendW}` : 'Not researched yet. Research reads its coverage and gives it an arrow, 1 seed.'}</span></div>
      {rd?.note && <div className="line"><span className="k">Driving it</span><span className="note">{rd.note}</span></div>}
      <div className="line"><span className="k">Season</span><span>{seaW}</span></div>
      {rd && (
        <div className="line"><span className="k">Reading</span><span>{rd.ago === 0 ? 'Today' : `${plural(rd.ago, 'day')} ago`}. Shared with every account holding this pill.
          {rd.ago > STALE_DAYS && <b className="amber"> Over 60 days: recheck to see if it is still trending.</b>}</span></div>
      )}
      <div className="acts">
        <button className="btn" type="button" onClick={() => { setWakeDraft(null); props.setSheet({ kind: 'pill', id: p.id, mode: 'clock' }) }}>
          {Icon.clock}Rest it{st.sea ? ` until ${fmt(sug)}` : ''}
        </button>
        <button className="btn" type="button" onClick={() => { close(); props.onResearch(p) }}>{rd ? 'Recheck' : 'Research'}<SeedCost n={SEED_COSTS.pill_research} /></button>
        <button className="btn ghost" type="button" disabled={busy} onClick={() => run(async () => {
          await props.onRemove(p); close(); toast(`${p.term} removed.`)
        })}>Remove from {label}</button>
      </div>
    </div>
  )
}

/** This pill's slice of its topic's share. */
function shareOf(p: ControlPill, entry: Entry | undefined, pills: ControlPill[]) {
  const v = entry?.share ?? 0
  const n = pills.filter((x) => x.topic === p.topic && x.pill_type !== 'anchor').length || 1
  return Math.max(1, Math.min(v, Math.round(v / n)))
}

/** A full pool: make room for a new pill, or for one whose season is back. */
function RoomPicker(props: SheetProps & { mode: Extract<SheetMode, { kind: 'room' }>; run: (f: () => Promise<void>) => Promise<void> }) {
  const { mode, pills, stOf, now, close, toast, run, pillCap, active } = props
  const waitingPill = mode.for === 'wake' ? props.pillById(mode.id) : undefined
  const topicId = mode.for === 'add' ? mode.topic : waitingPill?.topic ?? ''
  const list = pills
    .filter((p) => p !== waitingPill)
    .map((p) => ({ p, st: stOf(p) }))
    .filter((x) => !['resting', 'anchor', 'waiting', 'researching'].includes(x.st.s))
    .map((x) => ({ ...x, same: x.p.topic === topicId, weak: (x.st.r?.band ?? 0) * 2 + (x.st.r?.type === 'broad' ? 0 : 1) + (x.st.s === 'closing' ? -3 : 0) }))
    .sort((a, b) => Number(b.same) - Number(a.same) || a.weak - b.weak)
  const sugg = list[0]
  const same = list.filter((x) => x.same)
  const rest = list.filter((x) => !x.same).slice(0, 8)
  const label = (t: string) => getTopic(t)?.label ?? t

  const act = (x: (typeof list)[number], how: 'rest' | 'remove') => run(async () => {
    const until = suggestedWake(x.p, now)
    if (how === 'rest') await props.onRest(x.p, until)
    else await props.onRemove(x.p)
    const verb = how === 'rest' ? `rests until ${fmt(until)}` : 'removed'
    close()
    if (mode.for === 'wake' && waitingPill) toast(<>Following <b>{waitingPill.term}</b> again. {x.p.term} {verb}.</>, 3400)
    else if (mode.for === 'add') { props.onAddAfterRoom(mode.topic, mode.term); window.setTimeout(() => toast(<>{x.p.term} {verb}. Adding <b>{mode.term}</b>.</>, 3000), 50) }
  })

  const row = (x: (typeof list)[number]) => (
    <div className="row" key={x.p.id}>
      <div className="t">
        <b>{x.p.term}</b>
        <small className={x === sugg ? 'sug' : ''}>{label(x.p.topic)} · {BAND_SHORT[x.st.r?.band ?? 0]} coverage{x.st.s === 'closing' ? ' · season ending' : ''}{x === sugg ? ' · suggested' : ''}</small>
      </div>
      <button type="button" onClick={() => void act(x, 'rest')}>{Icon.clock}Rest</button>
      <button type="button" onClick={() => void act(x, 'remove')}>Remove</button>
    </div>
  )

  const sw = waitingPill ? seasonWord(waitingPill.term) : null
  const nm = sw ? cap1(sw.key) : waitingPill ? waitingPill.term : ''
  return (
    <div className="fx-in">
      {mode.for === 'add' ? (
        <>
          <div className="eb">Your pills are full · {active} of {pillCap}</div>
          <h2>Make room for {mode.term}</h2>
          <p className="lead">Rest one until later, or remove it. Resting pills don’t count.</p>
        </>
      ) : (
        <>
          <div className="eb">Season’s back · {label(topicId)}</div>
          <h2>{nm} season is starting.</h2>
          <p className="lead">Coverage usually starts building now. Follow <b>{waitingPill?.term}</b> again? Your pills are full ({active} of {pillCap}), so pick one to rest or remove.</p>
        </>
      )}
      {props.nextPlan && (
        <>
          <div className="up">
            <div className="t"><b>{props.nextPlan.name} holds {props.nextPlan.pills} pills</b><small>Keep everything you follow and add this one. ${props.nextPlan.price} a month.</small></div>
            <button type="button" onClick={() => { close(); props.onUpgrade() }}>Upgrade</button>
          </div>
          <div className="grp">Or make room</div>
        </>
      )}
      <div className="pick">
        {same.length > 0 && <><div className="grp">In {label(topicId)}</div>{same.map(row)}</>}
        {rest.length > 0 && <><div className="grp">Other topics</div>{rest.map(row)}</>}
      </div>
      <div className="acts">
        {mode.for === 'wake' && waitingPill && (
          <button className="btn" type="button" onClick={() => run(async () => {
            const until = suggestedWake(waitingPill, now)
            await props.onRest(waitingPill, until); close()
            toast(<><b>{waitingPill.term}</b> is resting until {fmt(until)}.</>)
          })}>Not this year · rest until {fmt(suggestedWake(waitingPill, now))}</button>
        )}
        <button className="btn ghost" type="button" onClick={close}>{mode.for === 'wake' ? 'Decide later' : 'Cancel'}</button>
      </div>
    </div>
  )
}

/** The account's content gates. Not on Brian's board; kept so they stay reachable. */
function Gates() {
  const { data: prefs } = useClientPrefs()
  const update = useUpdateClientPrefs()
  if (!prefs) return null
  return (
    <section className="fx-gates">
      <div className="k">Also shapes your feed</div>
      <div className="gate">
        <div><strong>Family friendly</strong><span>Leaves out fatalities, violent crime and graphic detail before anything is written.</span></div>
        <button role="switch" aria-checked={prefs.family_friendly} className="gg-switch" aria-label="Family friendly"
          onClick={() => update.mutate({ family_friendly: !prefs.family_friendly })} />
      </div>
      <div className="gate col">
        <div><strong>Politics</strong><span>
          {prefs.politics_mode === 'off' ? 'Off: no political stories at all.'
            : prefs.politics_mode === 'neutral' ? 'Neutral: political stories, written without taking a side.'
            : 'Open: political stories, and your voice may take a position.'}
        </span></div>
        <div className="seg" role="group" aria-label="Politics">
          {(['off', 'neutral', 'open'] as const).map((m) => (
            <button key={m} type="button" aria-pressed={prefs.politics_mode === m} onClick={() => update.mutate({ politics_mode: m })}>{cap1(m)}</button>
          ))}
        </div>
      </div>
      <p className="foot"><Link to={ROUTES.places}>Places</Link> decide where your feed looks; topics decide what it looks for.</p>
    </section>
  )
}
